import React, { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { C } from "../theme/pbi";
import AppSelect from "./ui/AppSelect";

/**
 * Self-contained, Power BI–styled date + time picker (no external dependency).
 * The popover is portaled to <body> and positioned against the viewport, so a
 * parent panel's `overflow:hidden` can never clip it; it flips above / shifts
 * left when it would run off-screen.
 * value: ISO string (or "") · onChange(iso|"") · placeholder.
 */
const WD = ["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"];
const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const POP_W = 300, POP_H = 402, GAP = 6, MARGIN = 8;
const pad = (n) => String(n).padStart(2, "0");
const MON3 = ["jan", "feb", "mar", "apr", "may", "jun", "jul", "aug", "sep", "oct", "nov", "dec"];

/**
 * Parse what an operator types into the field. The popover alone meant a cinema
 * entering a week of showtimes had to click a day grid and two dropdowns for
 * every single one — while the "More times, same day" box right below happily
 * accepted "15:30, 18:45, 10:00 pm". This closes that gap.
 *
 * Accepts: "04/08/2026 12:15" · "2026-08-04 12:15" · "4 Aug 2026 6:45 pm" ·
 * "Aug 4 2026 18:45" · "12:15" (keeps the chosen day) · the field's own
 * rendered value, so editing in place works.
 * Returns a Date, "" to clear, or null when it can't be read.
 */
const parseTyped = (raw, base) => {
  let str = String(raw || "").trim().toLowerCase().replace(/,/g, " ").replace(/\s+/g, " ");
  if (!str) return "";
  str = str.replace(/^(mon|tue|wed|thu|fri|sat|sun)[a-z]*\s+/, "");
  const ref = base || new Date();
  let hh = ref.getHours(), mm = ref.getMinutes();
  let datePart = str;
  const tm = str.match(/(\d{1,2}):(\d{2})\s*(am|pm)?$/);
  if (tm) {
    hh = Number(tm[1]); mm = Number(tm[2]);
    if (tm[3]) { if (hh === 12) hh = 0; if (tm[3] === "pm") hh += 12; }
    if (hh > 23 || mm > 59) return null;
    datePart = str.slice(0, tm.index).trim();
  }
  let y = ref.getFullYear(), mo = ref.getMonth(), d = ref.getDate();
  if (datePart) {
    const yr = (v) => (v ? (v.length === 2 ? 2000 + Number(v) : Number(v)) : ref.getFullYear());
    let mt;
    if ((mt = datePart.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/))) { y = +mt[1]; mo = +mt[2] - 1; d = +mt[3]; }
    else if ((mt = datePart.match(/^(\d{1,2})[/.-](\d{1,2})(?:[/.-](\d{2,4}))?$/))) { d = +mt[1]; mo = +mt[2] - 1; y = yr(mt[3]); }
    else if ((mt = datePart.match(/^(\d{1,2}) ([a-z]{3,})(?: (\d{2,4}))?$/))) { d = +mt[1]; mo = MON3.indexOf(mt[2].slice(0, 3)); y = yr(mt[3]); }
    else if ((mt = datePart.match(/^([a-z]{3,}) (\d{1,2})(?: (\d{2,4}))?$/))) { mo = MON3.indexOf(mt[1].slice(0, 3)); d = +mt[2]; y = yr(mt[3]); }
    else return null;
    if (mo < 0 || mo > 11 || d < 1 || d > 31) return null;
  }
  const out = new Date(y, mo, d, hh, mm);
  // Rejects 31 Feb and friends: the Date constructor rolls them over.
  return Number.isFinite(out.getTime()) && out.getDate() === d && out.getMonth() === mo ? out : null;
};
const sameDay = (a, b) => a && b && a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

const DateTimeField = ({ value, onChange, placeholder = "Pick date & time" }) => {
  const cur = value ? new Date(value) : null;
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null);
  const [view, setView] = useState(cur || new Date());
  const [h, setH] = useState(cur ? cur.getHours() : 19);
  const [m, setM] = useState(cur ? cur.getMinutes() : 0);
  // Non-null only while the operator is mid-edit, so the field otherwise stays
  // a pure render of `value`.
  const [typed, setTyped] = useState(null);
  const wrapRef = useRef(null);
  const popRef = useRef(null);

  useEffect(() => {
    setTyped(null);
    if (cur) { setH(cur.getHours()); setM(cur.getMinutes()); setView(cur); }
  }, [value]);

  const computePos = () => {
    const el = wrapRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    let left = Math.max(MARGIN, Math.min(r.left, window.innerWidth - POP_W - MARGIN));
    let top = r.bottom + GAP;
    const belowFits = r.bottom + POP_H + GAP <= window.innerHeight;
    const aboveFits = r.top - POP_H - GAP >= 0;
    if (!belowFits && aboveFits) top = r.top - POP_H - GAP;
    setPos({ left, top });
  };

  useEffect(() => {
    if (!open) return;
    computePos();
    const onMove = () => computePos();
    window.addEventListener("scroll", onMove, true);
    window.addEventListener("resize", onMove);
    const onDoc = (e) => {
      if (wrapRef.current && wrapRef.current.contains(e.target)) return;
      if (popRef.current && popRef.current.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => {
      window.removeEventListener("scroll", onMove, true);
      window.removeEventListener("resize", onMove);
      document.removeEventListener("mousedown", onDoc);
    };
  }, [open]);

  const emit = (d) => onChange(d ? d.toISOString() : "");
  const pickDay = (day) => emit(new Date(view.getFullYear(), view.getMonth(), day, h, m));
  // With no date chosen yet this used to move the dropdowns and emit nothing,
  // so the field stayed empty and Done looked broken. Fall back to the month
  // being viewed (today on first open).
  const setTime = (nh, nm) => {
    setH(nh); setM(nm);
    const base = cur || view || new Date();
    emit(new Date(base.getFullYear(), base.getMonth(), base.getDate(), nh, nm));
  };
  const shift = (delta) => setView(new Date(view.getFullYear(), view.getMonth() + delta, 1));

  const y = view.getFullYear(), mo = view.getMonth();
  const startDow = (new Date(y, mo, 1).getDay() + 6) % 7;
  const daysIn = new Date(y, mo + 1, 0).getDate();
  const today = new Date();
  const cells = [];
  for (let i = 0; i < startDow; i++) cells.push(null);
  for (let d = 1; d <= daysIn; d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);

  const display = cur
    ? cur.toLocaleString(undefined, { weekday: "short", day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })
    : "";

  const inputStyle = {
    background: C.inputBg, border: `1px solid ${open ? C.green : C.border}`, borderRadius: C.radiusSm,
    fontSize: 13, color: C.text, padding: "8px 34px 8px 11px", width: "100%", height: 38,
    outline: "none", fontFamily: "inherit",
  };
  const navBtn = { background: "none", border: "none", color: C.textMid, cursor: "pointer", fontSize: 16, lineHeight: 1, padding: "2px 6px", borderRadius: 6 };
  const dayBtn = (d) => {
    const isSel = cur && sameDay(cur, new Date(y, mo, d));
    const isToday = sameDay(today, new Date(y, mo, d));
    return {
      width: 32, height: 32, borderRadius: 8, border: isToday && !isSel ? `1px solid ${C.green}` : "1px solid transparent",
      background: isSel ? C.green : "transparent", color: isSel ? "#fff" : C.text,
      fontSize: 13, fontWeight: isSel ? 700 : 500, cursor: "pointer", transition: "background .1s",
    };
  };
  const timeSel = {
    background: C.inputBg, border: `1px solid ${C.border}`, borderRadius: C.radiusSm, color: C.text,
    fontSize: 13, padding: "6px 8px", height: 34, cursor: "pointer",
  };

  const popover = pos && (
    <div ref={popRef} style={{ position: "fixed", zIndex: 4000, top: pos.top, left: pos.left, width: POP_W, background: "#fff", border: `1px solid ${C.border}`, borderRadius: 12, boxShadow: "0 8px 30px rgba(26,43,74,0.22)", padding: 14 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
        <button type="button" style={navBtn} onClick={() => shift(-1)} aria-label="Previous month">‹</button>
        <div style={{ fontWeight: 700, fontSize: 14, color: C.text }}>{MONTHS[mo]} {y}</div>
        <button type="button" style={navBtn} onClick={() => shift(1)} aria-label="Next month">›</button>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 2, marginBottom: 4 }}>
        {WD.map((w) => <div key={w} style={{ textAlign: "center", fontSize: 10, fontWeight: 700, color: C.textLight, textTransform: "uppercase" }}>{w}</div>)}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 2, placeItems: "center" }}>
        {cells.map((d, i) => d ? (
          <button key={i} type="button" style={dayBtn(d)} onClick={() => pickDay(d)}
            onMouseOver={(e) => { if (!(cur && sameDay(cur, new Date(y, mo, d)))) e.currentTarget.style.background = C.greenSoft; }}
            onMouseOut={(e) => { if (!(cur && sameDay(cur, new Date(y, mo, d)))) e.currentTarget.style.background = "transparent"; }}>
            {d}
          </button>
        ) : <div key={i} style={{ width: 32, height: 32 }} />)}
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 12, paddingTop: 12, borderTop: `1px solid ${C.border}` }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: C.textMid, textTransform: "uppercase" }}>Time</span>
        <AppSelect value={h} onChange={(e) => setTime(Number(e.target.value), m)} style={timeSel}>
          {Array.from({ length: 24 }, (_, i) => <option key={i} value={i}>{pad(i)}</option>)}
        </AppSelect>
        <span style={{ color: C.textMid, fontWeight: 700 }}>:</span>
        <AppSelect value={m} onChange={(e) => setTime(h, Number(e.target.value))} style={timeSel}>
          {Array.from({ length: 60 }, (_, i) => <option key={i} value={i}>{pad(i)}</option>)}
        </AppSelect>
        <span style={{ fontSize: 11, color: C.textLight, marginLeft: "auto" }}>24h</span>
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 12 }}>
        <button type="button" style={{ ...navBtn, fontSize: 12, color: C.accent }} onClick={() => { onChange(""); setOpen(false); }}>Clear</button>
        <div style={{ display: "flex", gap: 6 }}>
          <button type="button" style={{ ...navBtn, fontSize: 12, color: C.blue }} onClick={() => { const n = new Date(); setView(n); setTime(n.getHours(), n.getMinutes()); emit(n); }}>Now</button>
          <button type="button" style={{ background: C.green, color: "#fff", border: "none", borderRadius: 6, fontSize: 12, fontWeight: 600, padding: "6px 16px", cursor: "pointer" }} onClick={() => setOpen(false)}>Done</button>
        </div>
      </div>
    </div>
  );

  // Read what was typed; silently revert when it can't be parsed so a stray
  // keystroke never wipes a date the operator already set.
  const commitTyped = () => {
    if (typed == null) return;
    const parsed = parseTyped(typed, cur);
    setTyped(null);
    if (parsed === null) return;
    if (parsed === "") { onChange(""); return; }
    emit(parsed);
    setView(parsed);
    setH(parsed.getHours());
    setM(parsed.getMinutes());
  };

  return (
    <div ref={wrapRef} style={{ position: "relative" }}>
      <input
        type="text"
        value={typed ?? display}
        placeholder={placeholder}
        aria-label={placeholder}
        onChange={(e) => setTyped(e.target.value)}
        onClick={() => setOpen(true)}
        onBlur={commitTyped}
        onKeyDown={(e) => {
          if (e.key === "Enter") { e.preventDefault(); commitTyped(); setOpen(false); }
          else if (e.key === "Escape") { setTyped(null); setOpen(false); }
        }}
        style={inputStyle}
      />
      <button
        type="button"
        aria-label="Open the calendar"
        onClick={(e) => { e.preventDefault(); setOpen((o) => !o); }}
        style={{ position: "absolute", right: 6, top: 6, height: 26, width: 26, border: "none", background: "none", cursor: "pointer", fontSize: 13, lineHeight: 1, padding: 0 }}
      >📅</button>
      {open && ReactDOM.createPortal(popover, document.body)}
    </div>
  );
};

export default DateTimeField;
